import { Identity } from "@ory/client";
import { useEffect, useState } from "react";
import AuthService from "services/AuthService/AuthService";
import { useAuthStore } from "stores/authStore";
import { ory, redirectToOryLogin } from "../../utils/ory";
import sentryService from "services/SentryService";
import * as Sentry from "@sentry/react";

const useOryAuth = () => {
  const setCurrentUserId = useAuthStore(state => state.setCurrentUserId);
  const setEmail = useAuthStore(state => state.setEmail);
  const setToken = useAuthStore(state => state.setToken);
  const setUseRights = useAuthStore(state => state.setUseRights);
  const setExpiresAt = useAuthStore(state => state.setExpiresAt);
  const setAvatar = useAuthStore(state => state.setAvatar);
  const setFullName = useAuthStore(state => state.setFullName);
  const [isLoginFailed, setIsLoginFailed] = useState(false);

  const getUserName = (identity: Identity): string =>
    identity.traits.email || identity.traits.username;

  const resetAuth = () => {
    useAuthStore.getState().reset();

    sentryService.setUser();
  };

  useEffect(() => {
    ory
      .toSession()
      .then(({ data: session }) => {
        const { identity } = session;

        if (!identity) {
          const msg = "Invalid session (no identity), redirect to login.";
          console.error(msg);

          Sentry.captureMessage(msg, {
            level: "warning",
            tags: { section: "auth", step: "toSession" },
            extra: { session },
          });

          redirectToOryLogin();
          return;
        }

        const username = getUserName(identity);
        setEmail(username);

        ory.createBrowserLogoutFlow().then(async () => {
          try {
            const { status, data } = await AuthService.signup(username);

            if (status === 200) {
              const result = data;
              result.token && setToken(result.token);
              result.id && setCurrentUserId(result.id);
              result.useRights && setUseRights(result.useRights);
              setAvatar(result.avatar);
              setFullName(result.fullName);
              session.expires_at && setExpiresAt(session.expires_at);

              sentryService.setUser(result);

              window.location.replace("/home");
            } else {
              Sentry.captureMessage("Signup returned non-200", {
                level: "warning",
                tags: { section: "auth", step: "signup" },
                extra: { status, data, username },
              });

              setIsLoginFailed(true);
            }
          } catch (e) {
            console.error(e);

            Sentry.captureException(e, {
              tags: { section: "auth", step: "signup" },
              extra: { username },
            });

            setIsLoginFailed(true);
          }
        });
      })
      .catch(err => {
        console.error("Invalid session, redirect to login..", err);

        Sentry.captureException(err, {
          tags: { section: "auth", step: "toSession" },
        });

        resetAuth();
        redirectToOryLogin();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { isLoginFailed };
};

export default useOryAuth;
